import React, { useMemo } from 'react';
import {
	DataTableSettingsPanel,
	ScatterChartPanel,
	BarChartPanel,
	SignificantChangesPanel,
	AbsoluteTrendBarPanel,
	GroupHighlightsPanel,
	ATScatterChartPanel,
} from '../';
import { getSelectedLocationName, getSelectedLocations, setUrlQueryParam } from '../../../../../../actions';
import i18n from '../../../../../../i18n';
import useCustomGroups from '../../../../../../hooks/useCustomGroups';
import customGroupsService from '../../../../../../services/customGroupsService';

export default function AdditionalSettingPanel({ urlParams, dispatch, state }) {
	const selectedLocations = getSelectedLocations(state);
	const { customGroupsArray } = useCustomGroups();
	const showCorrelationScatterGroupHighlights =
		urlParams.view === 'graph' && urlParams.subview === 'correlation' && urlParams.option === 'scatter';
	const groupHighlightOptions = useMemo(() => {
		const groupHighlightOptions = [];
		if (selectedLocations.length > 1 || showCorrelationScatterGroupHighlights) {
			state?.fullListOfCountryGroups.forEach((group) => {
				groupHighlightOptions.push({
					text: getSelectedLocationName(state, group.uid),
					value: group.uid,
				});
			});
			customGroupsArray.forEach((group) => {
				groupHighlightOptions.push({
					text: group.name,
					value: customGroupsService.urlUtils.generateUrlParam(group.id, group.name, group.countries),
					isCustom: true,
				});
			});
		}
		return groupHighlightOptions;
	}, [state.fullListOfCountryGroups, selectedLocations, showCorrelationScatterGroupHighlights, customGroupsArray]);

	const groupHighlights = {
		header: i18n.t('Highlight group'),
		onChange: (e) => {
			dispatch({
				type: urlParams.view === 'table' ? 'setDataTableFilters' : 'setUrlQueryParam',
				payload: setUrlQueryParam({ name: 'groupHighlight', value: e.target.value }),
			});
			return true;
		},
		options: groupHighlightOptions,
		selected: urlParams.groupHighlight || '',
	};

	switch (urlParams.view) {
		case 'table': {
			if (urlParams.subview === 'drivers') {
				return null;
			}
			if (urlParams.subview === 'changes') {
				return <SignificantChangesPanel />;
			}
			return (
				<DataTableSettingsPanel
					urlParams={urlParams}
					dispatch={dispatch}
					state={state}
					groupHighlights={groupHighlights}
				/>
			);
		}
		case 'graph': {
			if (urlParams.subview === 'absoluteTrend' && urlParams.option === 'bar') {
				return (
					<AbsoluteTrendBarPanel
						urlParams={urlParams}
						dispatch={dispatch}
						state={state}
						groupHighlights={groupHighlights}
					/>
				);
			}
			if (urlParams.subview === 'absoluteTrend' && urlParams.option === 'scatter') {
				return (
					<ATScatterChartPanel
						urlParams={urlParams}
						dispatch={dispatch}
						state={state}
						groupHighlights={groupHighlights}
					/>
				);
			}
			if (urlParams.subview === 'annualAvgTrend' && urlParams.option === 'scatter') {
				return (
					<ScatterChartPanel
						urlParams={urlParams}
						dispatch={dispatch}
						state={state}
						groupHighlights={groupHighlights}
					/>
				);
			}
			if (urlParams.subview === 'score' && urlParams.option === 'bar') {
				return (
					<BarChartPanel urlParams={urlParams} dispatch={dispatch} state={state} groupHighlights={groupHighlights} />
				);
			}
			if (urlParams.subview === 'score') {
				return <GroupHighlightsPanel groupHighlights={groupHighlights} />;
			}
			if (urlParams.subview === 'correlation' && urlParams.option === 'scatter') {
				return <GroupHighlightsPanel groupHighlights={groupHighlights} />;
			}
			if (urlParams.option === 'bar') {
				return (
					<BarChartPanel urlParams={urlParams} dispatch={dispatch} state={state} groupHighlights={groupHighlights} />
				);
			}
			return null;
		}
		case 'map': {
			return <GroupHighlightsPanel groupHighlights={groupHighlights} />;
		}
		default: {
			return null;
		}
	}
}
